import { ProductBadges } from "@components/ui/product-badges/ProductBadges";
import { Text } from "@ui/components/content/text/Text";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid } from "@ui/components/layout/grid/Grid";
import { Collapse } from "@ui/components/other/transitions/Collapse";
import { chakraRef } from "@ui/utils/forwardRef";
import { getImageAltTag } from "@ui/utils/productNameTransform";
import type { Dispatch, SetStateAction } from "react";
import { space } from "ui/src/design-tokens";
import type { Product, ProductTileProps } from "../ProductTile";
import { useRouter } from "i18n/routing";
import { ProductImageFrame } from "@ui/components/commerce/productImage/ProductImageFrame";
import { getValidSaleBadge } from "@lib/utils/pdp/saleBadgeUtils";

interface ProductImagesProps {
    initialProduct: Product;
    displayedProduct: Product;
    productColors: ProductTileProps["productColors"];
    productName: ProductTileProps["productName"];
    setDisplayedProduct: Dispatch<SetStateAction<Product>>;
    isExpanded: boolean;
    onOpen: () => void;
    onClose: () => void;
    altTagPattern?: string;
    productSlug?: string;
    discountPercentage: number;
    customBadges?: string[];
}

export const ProductImages = chakraRef(
    (
        {
            setDisplayedProduct,
            displayedProduct,
            initialProduct,
            productColors,
            isExpanded,
            onOpen,
            onClose,
            altTagPattern,
            productSlug,
            discountPercentage,
            customBadges,
        }: ProductImagesProps,
        ref
    ) => {
        const { push } = useRouter();

        const setCurrentProduct = (item: Product) => {
            setDisplayedProduct({
                ...displayedProduct,
                ...item,
            });
        };

        const setInitialProduct = () => {
            setDisplayedProduct(initialProduct);
        };

        const productColorsWithStock = productColors?.filter((item) => item.hasStock) ?? [];
        const additionalColors = `+${Math.max(productColorsWithStock.length - 5, 0)}`;
        const saleBadge = getValidSaleBadge(displayedProduct.eaBadge);

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                push(productSlug as string);
            }
        };

        return (
            <>
                {/* Main Product Image */}
                <Box
                    onMouseEnter={onOpen}
                    onMouseLeave={onClose}
                    sx={{
                        aspectRatio: "4/5",
                        position: "relative",
                        width: "100%",
                    }}
                >
                    <ProductImageFrame
                        src={displayedProduct?.image}
                        alt={getImageAltTag(altTagPattern, displayedProduct?.imageLabel)}
                        quality={80}
                    />
                    {(discountPercentage > 0 ||
                        customBadges?.length > 0 ||
                        displayedProduct?.eaBadge) && (
                        <ProductBadges
                            discount={
                                discountPercentage > 0 ? `-${discountPercentage}%` : undefined
                            }
                            customBadges={customBadges ?? []}
                            saleBadge={saleBadge}
                        />
                    )}
                </Box>

                {/* Color Swatches */}
                <Box
                    display={["none", "block"]}
                    pl={[4, 4, 4, 6]}
                    w="full"
                    onMouseEnter={onOpen}
                    onMouseLeave={onClose}
                    ref={ref}
                    pt={isExpanded ? 3 : 0}
                    transition="padding-top 0.3s"
                >
                    <Collapse
                        transition={{ enter: { duration: 0.3 }, exit: { duration: 0.3 } }}
                        in={isExpanded}
                        animateOpacity
                        startingHeight={0.01}
                    >
                        <Grid
                            data-testid="product-image-swatches"
                            templateColumns="repeat(auto-fit, minmax(15%, 15%))"
                            h="full"
                            gap={space["0.2"]}
                        >
                            {[initialProduct, ...productColorsWithStock.slice(0, 5)].map(
                                (item, index) => (
                                    <Box
                                        key={item?.objectID}
                                        gridColumn={index + 1}
                                        onMouseEnter={() => setCurrentProduct(item)}
                                        onMouseLeave={setInitialProduct}
                                        sx={{
                                            aspectRatio: "4/5",
                                            display: "flex",
                                            alignItems: "flex-end",
                                            width: "100%",
                                        }}
                                        role="link"
                                        tabIndex={0}
                                        onKeyDown={handleKeyDown}
                                        onFocus={() => setCurrentProduct(item)}
                                        onBlur={setInitialProduct}
                                        _focusVisible={{
                                            outline: "2px solid black",
                                            zIndex: 2,
                                            outlineOffset: "-2px",
                                        }}
                                    >
                                        <ProductImageFrame
                                            src={item?.image || ""}
                                            alt={
                                                getImageAltTag(altTagPattern, item?.imageLabel) ||
                                                ""
                                            }
                                        />
                                    </Box>
                                )
                            )}
                            {productColorsWithStock.length > 5 && (
                                <Flex gridColumn={7} justifyContent="center" alignItems="center">
                                    <Text size="large" fontWeight="bold" color="black">
                                        {additionalColors}
                                    </Text>
                                </Flex>
                            )}
                        </Grid>
                    </Collapse>
                </Box>
            </>
        );
    }
);

ProductImages.displayName = "ProductImages";
